/**
 * services/hire/requisitionV2.ts
 * Client for the sk-hire v2 requisitions ("Jobs") API. Uses the shared axios
 * instance (withCredentials — the platform session cookie) and the endpoint
 * constants. All calls are org-scoped server-side by the platform JWT.
 */
import axiosInstance from '../../api/axiosInstance';
import {
  HireMatch_Requisitions_Url,
  HireMatch_Requisition_Url,
  HireMatch_Requisition_Activate_Url,
  HireMatch_Requisition_Close_Url,
  HireMatch_Requisition_Reopen_Url,
  HireMatch_Requisition_Extract_Url,
  HireMatch_Requisition_ExtractFile_Url,
  HireMatch_Requisition_SourceFile_Url,
  HireMatch_Requisition_SourceFileDownload_Url,
} from '../../api/endpoints';
import type {
  RequisitionListItem,
  RequisitionListResult,
  RequisitionDetail,
  RequisitionInput,
  ExtractResult,
  ExtractFileResult,
} from '../../schemas/hire/requisitionV2';

const EMPTY_REQ_RESULT: RequisitionListResult = {
  items: [], page: 1, pageSize: 10, total: 0,
  counts: { all: 0, active: 0, draft: 0, on_hold: 0, closed: 0 },
};

export interface ListRequisitionsParams {
  q?: string;
  status?: string;
  page?: number;
  pageSize?: number;
}

/**
 * List requisitions (paginated). The server applies the relevance search (`q`),
 * status filter and pagination, returning the page plus aggregate per-status
 * counts. An optional AbortSignal cancels a stale request while typing/paging.
 */
export async function listRequisitions(params: ListRequisitionsParams = {}, signal?: AbortSignal): Promise<RequisitionListResult> {
  const term = params.q?.trim();
  const { data } = await axiosInstance.get<RequisitionListResult>(HireMatch_Requisitions_Url, {
    params: {
      ...(term ? { q: term } : {}),
      ...(params.status && params.status !== 'all' ? { status: params.status } : {}),
      ...(params.page ? { page: params.page } : {}),
      ...(params.pageSize ? { pageSize: params.pageSize } : {}),
    },
    signal,
  });
  return data && Array.isArray(data.items) ? data : EMPTY_REQ_RESULT;
}

export async function getRequisition(id: string): Promise<RequisitionDetail> {
  const { data } = await axiosInstance.get<RequisitionDetail>(HireMatch_Requisition_Url(id));
  return data;
}

/** Outcome of a create: `duplicate` means a live job already covers this title
 *  for the same client. The job is still created — the flag is advisory. */
export interface CreateRequisitionResult {
  result: 'created' | 'duplicate';
  requisition: RequisitionDetail;
  duplicateOfCode?: string;
}

export async function createRequisition(input: RequisitionInput): Promise<CreateRequisitionResult> {
  const { data } = await axiosInstance.post<CreateRequisitionResult>(HireMatch_Requisitions_Url, input);
  return data;
}

export async function updateRequisition(id: string, input: RequisitionInput): Promise<RequisitionDetail> {
  const { data } = await axiosInstance.patch<RequisitionDetail>(HireMatch_Requisition_Url(id), input);
  return data;
}

export async function activateRequisition(id: string): Promise<RequisitionDetail> {
  const { data } = await axiosInstance.post<RequisitionDetail>(HireMatch_Requisition_Activate_Url(id));
  return data;
}

/** Close (archive) a job — append-only, sets status → 'closed'. No hard delete. */
export async function closeRequisition(id: string): Promise<RequisitionDetail> {
  const { data } = await axiosInstance.post<RequisitionDetail>(HireMatch_Requisition_Close_Url(id));
  return data;
}

/**
 * Reopen a closed job (closed → active).
 *
 * Exists because the last placement now closes a job on its own: without an undo,
 * one mis-click on "Placed" would archive a live requisition permanently. Takes an
 * active-openings slot server-side, exactly as activation does.
 */
export async function reopenRequisition(id: string): Promise<RequisitionDetail> {
  const { data } = await axiosInstance.post<RequisitionDetail>(HireMatch_Requisition_Reopen_Url(id));
  return data;
}

export async function extractJd(text: string): Promise<ExtractResult> {
  const { data } = await axiosInstance.post<ExtractResult>(HireMatch_Requisition_Extract_Url, { text });
  return data;
}

/** Upload a source document (PDF/CSV/Excel): server parses → extracts → stores
 *  the original in SK-Drive. Returns the extracted fields + the Drive file id. */
/** `source: 'bulk_import'` tags the metered call's CHANNEL, so the AI Usage
 *  screen can separate a bulk run's spend from the same extraction done singly. */
export async function extractJdFile(
  input: { filename: string; contentBase64: string; contentType: string },
  source?: 'bulk_import',
): Promise<ExtractFileResult> {
  const { data } = await axiosInstance.post<ExtractFileResult>(
    HireMatch_Requisition_ExtractFile_Url, input, source ? { params: { source } } : undefined,
  );
  return data;
}

/** Signed download URL for a job's stored source document (scan-gated server-side). */
export async function getRequisitionSourceFile(id: string): Promise<{ url: string; filename: string | null }> {
  const { data } = await axiosInstance.get<{ url: string; filename: string | null }>(HireMatch_Requisition_SourceFile_Url(id));
  return data;
}

/** The source-document BYTES, streamed same-origin as an attachment so the
 *  browser saves the file instead of rendering the PDF in a tab. */
export async function downloadRequisitionSourceFile(id: string): Promise<Blob> {
  const { data } = await axiosInstance.get<Blob>(HireMatch_Requisition_SourceFileDownload_Url(id), { responseType: 'blob' });
  return data;
}
